// AccessBody — a person's access as ONE body (ADR-050 §2), shared by four hosts:
// the person page's "Edit access" drawer, the create wizard's Access step, the
// Add-a-person drawer and (read) the review. Two parts: ROLE — Administrator
// (only an admin may grant it), or the role on the family's picker with the ⓘ
// beside it (what the role allows — the shared capability summary) — over SCOPE,
// the sections (`ScopeFields`). Administrator on hides everything below: an admin
// carries no role and no scope. It OWNS the admin ⇄ role/scope exclusion (mirrors
// the backend's chk_upr_admin_xor_role) so no host re-implements it. A CONTROLLED
// component over one `AccessValues`; each host maps its RHF form onto
// value/onChange.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Picker } from '../../components/patterns/Picker.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { rolesSource, useRole } from '../../hooks/useRoles.js';
import { RoleHint } from '../../components/patterns/capabilities/RoleHint.js';
import { ScopeFields } from './ScopeFields.js';
import { EMPTY_SCOPE, type ScopeFormValues, type ScopeGiver, type ScopeOutside } from './scope.js';

export interface AccessValues {
  is_project_admin: boolean;
  role_id: number | null;
  scope: ScopeFormValues;
}

export interface AccessBodyProps {
  projectId: string;
  value: AccessValues;
  onChange: (next: AccessValues) => void;
  /** Show the Administrator switch ONLY when the actor is admin/sysadmin in the project (a delegate can't grant admin). */
  canGrantAdmin: boolean;
  /** The resolver's message for the role field (a non-admin needs a role). */
  roleError?: string;
  /** What the viewer may switch to "All" — the read's `giver`, or the viewer's own standing for a new person. */
  giver: ScopeGiver;
  /** The grants outside the viewer's reach, kept (an existing person). */
  outside?: ScopeOutside;
  /** The vehicles module is on — the Vehicle pools section exists. */
  vehicles: boolean;
  /** The field-force module is on — the Teams section may exist: it shows when the picked role carries a team key
   *  or the `team` responsibility level (ADR-083 §11), read from the role's detail here, so no host re-derives it. */
  fieldForce?: boolean;
  disabled?: boolean;
}

/** The Teams section's rule (ADR-083 §11): the role carries `team:read` / `team:manage`, or may name chains at the team level. */
export function roleOpensTeams(role: { permissions: readonly string[]; responsibility_levels: readonly string[] } | undefined): boolean {
  return !!role && (role.permissions.some((k) => k === 'team:read' || k === 'team:manage') || role.responsibility_levels.includes('team'));
}

/** The rule read LIVE from the role being saved (never a read's old role): off while the module is off or the person
 *  is an administrator (no role, no scope) — the editor's section, its footer's words and the wizard's review agree. */
export function useRoleOpensTeams(projectId: string, v: { fieldForce: boolean; isAdmin: boolean; roleId: number | null }): boolean {
  const role = useRole(projectId, v.fieldForce && !v.isAdmin ? v.roleId : null);
  return v.fieldForce && roleOpensTeams(role.data);
}

export function AccessBody({ projectId, value, onChange, canGrantAdmin, roleError, giver, outside, vehicles, fieldForce = false, disabled = false }: AccessBodyProps) {
  const { t } = useTranslation('users');
  const source = useMemo(() => rolesSource(projectId), [projectId]);
  const isAdmin = value.is_project_admin;
  const teams = useRoleOpensTeams(projectId, { fieldForce, isAdmin, roleId: value.role_id });

  // Administrator on: no role, no scope (the backend stores both empty); off: the role is left to pick.
  const setAdmin = (on: boolean) => onChange(on ? { is_project_admin: true, role_id: null, scope: EMPTY_SCOPE } : { ...value, is_project_admin: false });

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        {canGrantAdmin && <Toggle checked={isAdmin} onChange={setAdmin} label={t('access.admin')} disabled={disabled} />}
        {!isAdmin && (
          <div className="flex items-end gap-1">
            <div className="min-w-0 flex-1">
              <Picker
                source={source}
                label={t('access.role')}
                placeholder={t('access.rolePlaceholder')}
                required
                value={value.role_id != null ? String(value.role_id) : null}
                onChange={(v) => onChange({ ...value, is_project_admin: false, role_id: v != null ? Number(v) : null })}
                error={roleError}
                disabled={disabled}
              />
            </div>
            {/* The ⓘ — what the picked role allows; it sits beside the field, never inside its label. */}
            <div className="pb-1">
              <RoleHint projectId={projectId} roleId={value.role_id} />
            </div>
          </div>
        )}
      </section>

      {!isAdmin && (
        <section className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">{t('access.scope')}</h3>
          <ScopeFields projectId={projectId} value={value.scope} onChange={(scope) => onChange({ ...value, scope })} giver={giver} outside={outside} vehicles={vehicles} teams={teams} disabled={disabled} />
        </section>
      )}
    </div>
  );
}
